import { useState, useEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';
import colors from './colors';
import breakpoints from './breakpoints';
import itvThemeColors from './ITVTheme/colors';

export interface Theme {
  colors: typeof colors;
  breakpoints: typeof breakpoints;
  itvThemeColors: typeof itvThemeColors;
}

declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const useBreakPoints = () => {
  const { breakpoints } = useTheme();

  const [breakpointState, setBreakpointState] = useState(
    Object.entries(breakpoints).reduce(
      (rv, curr) => ({ ...rv, [curr[0]]: false }),
      {}
    ) as Record<keyof typeof breakpoints, boolean>
  );

  useEffect(() => {
    const subscriptions: (() => void)[] = [];

    for (const breakpoint of Object.entries(breakpoints)) {
      const match = window.matchMedia(`(max-width: ${breakpoint[1]}px)`);

      const handler = (state: { matches: boolean }) => {
        setBreakpointState((prev) => ({
          ...prev,
          [breakpoint[0]]: state.matches,
        }));
      };

      match.addListener(handler);
      handler(match);

      subscriptions.push(() => match.removeListener(handler));
    }

    return () => subscriptions.forEach((x) => x());
  }, [breakpoints]);

  return breakpointState;
};

interface ThemeProps {
  theme: Theme;
}

const mobileMediaQuery = ({ theme }: ThemeProps) =>
  `@media (max-width: ${theme.breakpoints.mobile}px)`;

const tabletMediaQuery = ({ theme }: ThemeProps) =>
  `@media (max-width: ${theme.breakpoints.tablet}px)`;

type ColorName = keyof typeof colors;

type ITVThemeColorName = keyof typeof itvThemeColors;

const itvThemeColor =
  (color: ITVThemeColorName) =>
  ({ theme }: ThemeProps) =>
    theme.itvThemeColors[color];

const themeColor =
  (color: ColorName) =>
  ({ theme }: ThemeProps) =>
    theme.colors[color];

interface BoxShadowProps {
  offset: number;
  blur: number;
  opacity: number;
  spread: number;
  color: string;
}

const boxShadow = ({
  offset = 3,
  blur = 7,
  opacity = 0.25,
  spread = 0,
  color,
}: Partial<BoxShadowProps> = {}) => {
  return `box-shadow: ${offset}px ${offset}px ${blur}px ${spread}px ${
    color ?? `rgba(0, 0, 0, ${opacity})`
  };`;
};

export const CustomSpacing = styled.div`
  margin-bottom: 100px;
`;

const accessibilityFocusStyleLight = `
&:focus-visible {
  outline: 3px solid #fff;
  text-decoration: none;
}
`;

const accessibilityFocusStyleDark = `
&:focus-visible {
  outline: 3px solid #051C1C;
  text-decoration: none; 
}
`;

const theme: Theme = {
  colors,
  breakpoints,
  itvThemeColors,
};

export {
  theme,
  mobileMediaQuery,
  tabletMediaQuery,
  themeColor,
  useBreakPoints,
  boxShadow,
  itvThemeColor,
  accessibilityFocusStyleDark,
  accessibilityFocusStyleLight,
};

export const FullBleed = css`
  box-shadow: 0 0 0 100vmax ${themeColor('darkBlue')};
  clip-path: inset(0 -100vmax);
`;
